import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Card,
  CircularProgress,
  Container,
  Grid,
  Typography
} from '@mui/material';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { UserContext } from 'src/contexts/userContext';
import { JSONRecordData } from 'src/content/dashboards/Log/graph.util';
import { FormattedMessage } from 'react-intl';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import Log from '../Log/Log';
import CancelIcon from '@mui/icons-material/Cancel';
import { UserType } from '../../../interfaces/UserTypes';
import { PARTNER_TICKETS_ENABLED } from '../../../config/featureFlags';
import HistoryOfActions from '../History/History';
import BuildIcon from '@mui/icons-material/Build';
import AccessContextProvider from '../../../contexts/AccessContextProvider';
import Access from '../ManageAccess/Access';
import InformationSodistorehome from '../Information/InformationSodistoreHome';
import { TimeSpan, UnixTime } from '../../../dataCache/time';
import { fetchDataJson } from '../Installations/fetchData';
import { FetchResult } from '../../../dataCache/dataCache';
import BatteryViewSodioHome from '../BatteryView/BatteryViewSodioHome';
import SodistoreHomeConfigurationV2 from './SodistoreHomeConfigurationV2';
import TopologySodistoreHome from '../Topology/TopologySodistoreHome';
import Overview from '../Overview/overview';
import WeeklyReport from './WeeklyReport';
import InstallationTicketsTab from '../Tickets/InstallationTicketsTab';
import DocumentsTab from '../Documents/DocumentsTab';
import InstallationChecklistTab from '../Checklist/InstallationChecklistTab';
import { CHECKLIST_ENABLED_PRODUCTS } from 'src/interfaces/ChecklistTypes';
import OnSiteChecklistTab from '../OnSiteChecklist/OnSiteChecklistTab';

interface singleInstallationProps {
  current_installation?: I_Installation;
  type?: string;
}

function SodioHomeInstallation(props: singleInstallationProps) {
  if (props.current_installation == undefined) {
    return null;
  }
  const S3data = {
    s3Region: props.current_installation.s3Region,
    s3Provider: props.current_installation.s3Provider,
    s3Key: props.current_installation.s3Key,
    s3Secret: props.current_installation.s3Secret,
    s3BucketId: props.current_installation.s3BucketId
  };

  const s3Bucket =
    props.current_installation.s3BucketId.toString() +
    '-' +
    (props.current_installation.product === 5
      ? '325c9373-9025-4a8d-bf5a-f9eedf1f155c'
      : 'e7b9a240-3c5d-4d2e-a019-6d8b1f7b73fa');

  const context = useContext(UserContext);
  const { currentUser } = context;
  const location = useLocation().pathname;
  const [errorLoadingS3Data, setErrorLoadingS3Data] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>(undefined);
  const [values, setValues] = useState<JSONRecordData | null>(null);
  const status = props.current_installation.status;
  const dataCollectionDisabled = props.current_installation.dataCollectionEnabled === false;
  const [
    failedToCommunicateWithInstallation,
    setFailedToCommunicateWithInstallation
  ] = useState(0);
  const [connected, setConnected] = useState(true);
  const [loading, setLoading] = useState(true);
  const [fetchFunctionCalled, setFetchFunctionCalled] = useState(false);
  const s3Credentials = { s3Bucket, ...S3data };

  //In React, useRef creates a mutable object that persists across renders without triggering re-renders when its value changes.
  //While fetching, we check the value of continueFetching, if its false, we break. This means that either the user changed tab or the object has been finished its execution (return)
  const continueFetching = useRef(false);

  function timeout(delay: number) {
    return new Promise((res) => setTimeout(res, delay));
  }

  const API_BASE_URL =
    'https://www.biwattpower.com/gateway/admin/open/device/currentEnergyFlowData/';

  async function encryptAES_CBC(data: string, secretKey: string) {
    const encoder = new TextEncoder();
    const keyBuffer = encoder.encode(secretKey.padEnd(32, ' ')); // Ensure 256-bit key

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-CBC' },
      false,
      ['encrypt']
    );

    // Generate a random IV (initialization vector)
    const iv = crypto.getRandomValues(new Uint8Array(16));
    const dataBuffer = encoder.encode(
      data.padEnd(Math.ceil(data.length / 16) * 16, '\0')
    );

    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-CBC', iv },
      cryptoKey,
      dataBuffer
    );

    // Convert encrypted data + IV to Base64
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const combined = new Uint8Array(iv.length + encryptedArray.length);
    combined.set(iv);
    combined.set(encryptedArray, iv.length);

    return btoa(String.fromCharCode(...combined));
  }

  // Probe multiple timestamps in parallel, return first successful result
  const probeTimestampBatch = async (
    offsets: number[]
  ): Promise<{ res: any; timestamp: UnixTime } | null> => {
    const now = UnixTime.now();
    const promises = offsets.map(async (offset) => {
      const ts = now.earlier(TimeSpan.fromSeconds(offset));
      const result = await fetchDataJson(ts, s3Credentials, false);
      if (result !== FetchResult.notAvailable && result !== FetchResult.tryLater) {
        return { res: result, timestamp: ts };
      }
      return null;
    });

    const results = await Promise.all(promises);
    // Return the most recent hit (smallest offset = first in array)
    return results.find((r) => r !== null) || null;
  };

  const fetchDataPeriodically = async () => {
    let res;
    let timestampToFetch: UnixTime;

    // Search backward in parallel batches of 10 timestamps (2s apart)
    // Each batch covers 20 seconds, so 20 batches cover 400 seconds
    const batchSize = 10;
    const step = 2; // 2-second steps to match even-rounding granularity
    const maxAge = 400;
    let found = false;

    for (let batchStart = 0; batchStart < maxAge; batchStart += batchSize * step) {
      if (!continueFetching.current) {
        return false;
      }
      const offsets = [];
      for (let j = 0; j < batchSize; j++) {
        const offset = batchStart + j * step;
        if (offset < maxAge) offsets.push(offset);
      }

      try {
        const hit = await probeTimestampBatch(offsets);
        if (hit) {
          res = hit.res;
          timestampToFetch = hit.timestamp;
          found = true;
          break;
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        return false;
      }
    }

    if (!found) {
      setConnected(false);
      setLoading(false);
      return false;
    }
    setConnected(true);
    setLoading(false);

    while (continueFetching.current) {
      for (const timestamp of Object.keys(res)) {
        if (!continueFetching.current) {
          setFetchFunctionCalled(false);
          return false;
        }
        console.log(`Timestamp: ${timestamp}`);
        const { Config: { S3: { Key, Secret, ...s3Rest } = {} as any, ...configRest } = {} as any, ...dataRest } = res[timestamp] || {};
        console.log({ ...dataRest, Config: { ...configRest, S3: { ...s3Rest, Key: '***', Secret: '***' } } });

        setValues(res[timestamp]);
        await timeout(2000);
      }

      // Advance by 150s to find the next chunk (15 records × 10s interval)
      timestampToFetch = timestampToFetch.later(TimeSpan.fromSeconds(150));
      console.log('NEW TIMESTAMP TO FETCH IS ' + timestampToFetch);

      let foundNext = false;
      for (var i = 0; i < 60; i++) {
        if (!continueFetching.current) {
          return false;
        }

        try {
          console.log('Trying to fetch timestamp ' + timestampToFetch);
          res = await fetchDataJson(timestampToFetch, s3Credentials, false);
          if (
            res !== FetchResult.notAvailable &&
            res !== FetchResult.tryLater
          ) {
            foundNext = true;
            break;
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          return false;
        }
        timestampToFetch = timestampToFetch.later(TimeSpan.fromSeconds(1));
      }
      if (!foundNext) {
        return false;
      }
    }
  };

  const fetchDataForOneTime = async () => {
    // Search backward in parallel batches of 10 timestamps (2s apart)
    const batchSize = 10;
    const step = 2;
    const maxAge = 400;
    let res;

    for (let batchStart = 0; batchStart < maxAge; batchStart += batchSize * step) {
      const offsets = [];
      for (let j = 0; j < batchSize; j++) {
        const offset = batchStart + j * step;
        if (offset < maxAge) offsets.push(offset);
      }

      try {
        const hit = await probeTimestampBatch(offsets);
        if (hit) {
          res = hit.res;
          break;
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        return false;
      }
    }

    if (!res) {
      setConnected(false);
      setLoading(false);
      return false;
    }

    setConnected(true);
    setLoading(false);

    // Sort timestamps numerically to ensure we get the most recent data point
    const timestamps = Object.keys(res).sort((a, b) => Number(b) - Number(a));
    const latestTimestamp = timestamps[0];
    setValues(res[latestTimestamp]);
    return true;
  };

  useEffect(() => {
    let path = location.split('/');
    setCurrentTab(path[path.length - 1]);
  }, [location]);

  useEffect(() => {
    if (status === null) {
      setConnected(false);
    }
  }, [status]);

  // The Information tab reads the device-reported Local IP out of `values`, but it is
  // not one of the tabs that fetch S3. Landing on Live first fills `values` for us, so
  // only deep-links / hard reloads straight to /information need a read of their own.
  // A monotonic run id, not a cancelled flag: the cleanup bumps it, which makes every
  // await still pending inside the abandoned run a no-op. A shared boolean could not do
  // this — re-entering the tab had to reset it, which un-cancelled the run the previous
  // cleanup had just aborted, so tab-flicking stacked concurrent sweeps and let an older
  // run's setValues clobber a fresher chunk.
  const infoFetchGeneration = useRef(0);
  // Set only by a run that reached its end. Keeps a completed miss from re-sweeping on
  // every revisit, while a run abandoned mid-flight is still allowed to retry.
  const infoFetchCompleted = useRef(false);
  const [infoFetchSettled, setInfoFetchSettled] = useState(false);

  // Deliberately NOT fetchDataForOneTime(): that one owns the shared `connected` and
  // `loading` flags, and a failed Information read setting them made the Live tab open
  // straight onto Topology's "Unable to communicate" banner while its own probe was
  // still starting. This writes `values` and nothing else.
  const fetchDeviceRecordForInformation = async (generation: number) => {
    const isCurrent = () => infoFetchGeneration.current === generation;
    const batchSize = 10;
    const step = 2;
    const maxAge = 400;

    try {
      for (
        let batchStart = 0;
        batchStart < maxAge;
        batchStart += batchSize * step
      ) {
        if (!isCurrent()) return;
        const offsets = [];
        for (let j = 0; j < batchSize; j++) {
          const offset = batchStart + j * step;
          if (offset < maxAge) offsets.push(offset);
        }

        const hit = await probeTimestampBatch(offsets);
        if (!isCurrent()) return;
        if (hit) {
          const timestamps = Object.keys(hit.res).sort(
            (a, b) => Number(b) - Number(a)
          );
          // A 200 that decodes to no records is a miss: keep searching the remaining
          // batches instead of ending the sweep on setValues(undefined).
          if (timestamps.length > 0) {
            setValues(hit.res[timestamps[0]]);
            break;
          }
        }
      }
    } catch (err) {
      // A throw is not a verdict on the installation, so leave infoFetchCompleted false:
      // the field settles, but revisiting the tab is still allowed to try again.
      console.error('Error reading device record for the Information tab:', err);
      if (isCurrent()) setInfoFetchSettled(true);
      return;
    }

    if (!isCurrent()) return;
    infoFetchCompleted.current = true;
    setInfoFetchSettled(true);
  };

  useEffect(() => {
    if (currentTab != 'information') {
      return;
    }
    // dataCollectionDisabled makes Information the DEFAULT tab (see the catch-all
    // Navigate below), and nothing is ever uploaded for those installations — probing
    // would burn ~200 guaranteed-miss GETs on every single visit.
    if (dataCollectionDisabled) {
      setInfoFetchSettled(true);
      return;
    }
    if (values != null || infoFetchCompleted.current) {
      return;
    }
    setInfoFetchSettled(false);
    fetchDeviceRecordForInformation(infoFetchGeneration.current);

    // Leaving the tab abandons the run in flight. Coming back is the retry gesture,
    // and only ever one run is live at a time.
    return () => {
      infoFetchGeneration.current += 1;
    };
    // dataCollectionDisabled is a dep, not just a guard: flipping data collection on
    // while sitting on this tab has to re-trigger the read, or the field stays stuck on
    // its empty verdict.
  }, [currentTab, dataCollectionDisabled]);

  useEffect(() => {
    if (
      currentTab == 'live' ||
      currentTab == 'pvview' ||
      currentTab == 'configuration' ||
      currentTab == 'overview' ||
      location.includes('batteryview')
    ) {
      //Fetch periodically if the tab is live, pvview or batteryview
      if (
        currentTab == 'live' ||
        currentTab == 'pvview' ||
        (location.includes('batteryview') && !location.includes('mainstats'))
      ) {
        if (!continueFetching.current) {
          continueFetching.current = true;
          //Call the function only one time. When the location and the currentTab change, this useEffect will be called 2 times
          if (!fetchFunctionCalled) {
            setFetchFunctionCalled(true);
            fetchDataPeriodically();
          }
        }
      }
      // Fetch one time in overview tab to determine connectivity
      if (currentTab == 'overview') {
        fetchDataForOneTime();
        return () => {
          continueFetching.current = false;
        };
      }
      // Fetch periodically in configuration tab to detect S3 config updates
      if (currentTab == 'configuration') {
        fetchDataForOneTime();

        const configRefreshInterval = setInterval(() => {
          fetchDataForOneTime();
        }, 30000);

        return () => {
          continueFetching.current = false;
          clearInterval(configRefreshInterval);
        };
      }

      return () => {
        continueFetching.current = false;
      };
    } else {
      //If the tab is not live, pvview, batteryview or configuration, then stop fetching.
      continueFetching.current = false;
    }
  }, [currentTab, location]);

  return (
    <>
      <Grid item xs={12} md={12}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            fontWeight="bold"
            color="text.primary"
            noWrap
            sx={{
              marginTop: '-20px',
              marginBottom: '10px',
              fontSize: '14px'
            }}
          >
            <FormattedMessage
              id="installation_name_simple"
              defaultMessage="Installation Name:"
            />
          </Typography>
          <Typography
            fontWeight="bold"
            color="orange"
            noWrap
            sx={{
              marginTop: '-20px',
              marginBottom: '10px',
              marginLeft: '5px',
              fontSize: '14px'
            }}
          >
            {props.current_installation.name}
          </Typography>
        </div>

        {currentTab == 'live' && values && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              fontWeight="bold"
              color="text.primary"
              noWrap
              sx={{
                marginTop: '0px',
                marginBottom: '10px',
                fontSize: '14px'
              }}
            >
              <FormattedMessage id="mode" defaultMessage="Mode:" />
            </Typography>
            <Typography
              fontWeight="bold"
              color="orange"
              noWrap
              sx={{
                marginTop: '0px',
                marginBottom: '10px',
                marginLeft: '85px',
                fontSize: '14px'
              }}
            >
              {values.InverterRecord?.OperatingPriority
                ?? values.InverterRecord?.WorkingMode
                ?? values.InverterRecord?.OperatingMode
                ?? values.Config?.OperatingPriority}
            </Typography>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            fontWeight="bold"
            color="text.primary"
            noWrap
            sx={{
              marginTop: '0px',
              marginBottom: '10px',
              fontSize: '14px'
            }}
          >
            Status:
          </Typography>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '75px',
              marginTop: '-10px'
            }}
          >
            {dataCollectionDisabled ? (
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '2px',
                  borderRadius: '50%',
                  backgroundColor: 'grey'
                }}
              />
            ) : (
              <>
                {status === -1 ? (
                  <CancelIcon
                    style={{
                      width: '23px',
                      height: '23px',
                      color: 'red',
                      borderRadius: '50%'
                    }}
                  />
                ) : (
                  ''
                )}

                {status === -2 ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: '#f7b34d'
                    }}
                  />
                ) : (
                  ''
                )}

                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    marginLeft: '2px',
                    borderRadius: '50%',
                    backgroundColor:
                      status === 2
                        ? 'red'
                        : status === 1
                        ? 'orange'
                        : status === -1 || status === -2
                        ? 'transparent'
                        : 'green'
                  }}
                />
              </>
            )}

            {props.current_installation.testingMode && (
              <BuildIcon
                style={{
                  width: '23px',
                  height: '23px',
                  color: 'purple',
                  borderRadius: '50%',
                  position: 'relative',
                  zIndex: 1,
                  marginLeft: '15px'
                }}
              />
            )}
          </div>
        </div>
        {loading &&
          !dataCollectionDisabled &&
          currentTab != 'information' &&
          // currentTab != 'manage' &&
          currentTab != 'history' &&
          currentTab != 'log' &&
          currentTab != 'report' &&
          currentTab != 'installationTickets' &&
          currentTab != 'documents' &&
          currentTab != 'checklist' &&
          currentTab != 'onsiteChecklist' && (
            <Container
              maxWidth="xl"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '70vh'
              }}
            >
              <CircularProgress size={60} style={{ color: '#ffc04d' }} />
              <Typography
                variant="body2"
                style={{ color: 'black', fontWeight: 'bold' }}
                mt={2}
              >
                <FormattedMessage id="connectingToDevice" defaultMessage="Connecting to the device..." />
              </Typography>
            </Container>
          )}

        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={routes.information}
                element={
                  <InformationSodistorehome
                    values={props.current_installation}
                    s3Credentials={s3Credentials}
                    type={props.type}
                    localIp={(values?.Config as any)?.LocalIp}
                    deviceDataAvailable={values != null}
                    deviceProbeSettled={infoFetchSettled}
                  ></InformationSodistorehome>
                }
              />

              {currentUser.userType !== UserType.client && !dataCollectionDisabled && (
                <Route
                  path={routes.log}
                  element={
                    <Log
                      errorLoadingS3Data={errorLoadingS3Data}
                      id={props.current_installation.id}
                      status={props.current_installation.status}
                    ></Log>
                  }
                />
              )}

              {!dataCollectionDisabled && (
                <Route
                  path={routes.live}
                  element={
                    <TopologySodistoreHome
                      values={values}
                      connected={connected}
                      loading={loading}
                      batteryClusterNumber={props.current_installation.batteryClusterNumber}
                    ></TopologySodistoreHome>
                  }
                />
              )}

              {currentUser.userType !== UserType.client && !dataCollectionDisabled && (
                <Route
                  path={routes.batteryview + '/*'}
                  element={
                    <BatteryViewSodioHome
                      values={values}
                      s3Credentials={s3Credentials}
                      installationId={props.current_installation.id}
                      installation={props.current_installation}
                      connected={connected}
                    ></BatteryViewSodioHome>
                  }
                />
              )}

              {currentUser.userType == UserType.admin && (
                <Route
                  path={routes.history}
                  element={
                    <HistoryOfActions
                      errorLoadingS3Data={errorLoadingS3Data}
                      id={props.current_installation.id}
                    ></HistoryOfActions>
                  }
                />
              )}

              {currentUser.userType == UserType.admin && !dataCollectionDisabled && (
                <Route
                  path={routes.configuration}
                  element={
                    <SodistoreHomeConfigurationV2
                      values={values}
                      id={props.current_installation.id}
                      installation={props.current_installation}
                    />
                  }
                />
              )}

              {/* {currentUser.userType == UserType.admin && (
                <Route
                  path={routes.manage}
                  element={
                    <AccessContextProvider>
                      <Access
                        currentResource={props.current_installation}
                        resourceType={props.type}
                      ></Access>
                    </AccessContextProvider>
                  }
                />
              )} */}

              {!dataCollectionDisabled && (
                <Route
                  path={routes.overview}
                  element={
                    <Overview
                      s3Credentials={s3Credentials}
                      id={props.current_installation.id}
                      device={props.current_installation.device}
                      product={props.current_installation.product}
                      connected={connected}
                      loading={loading}
                    />
                  }
                />
              )}

              {props.current_installation.device !== 3 && !dataCollectionDisabled && (
                <Route
                  path={routes.report}
                  element={
                    <WeeklyReport
                      installationId={props.current_installation.id}
                      installationName={props.current_installation.name}
                      installationEmail={props.current_installation.email}
                    />
                  }
                />
              )}

              {(currentUser.userType == UserType.admin ||
                PARTNER_TICKETS_ENABLED) && (
                <Route
                  path={routes.installationTickets}
                  element={
                    <InstallationTicketsTab
                      installationId={props.current_installation.id}
                    />
                  }
                />
              )}

              {/* Documents tab is open to every role: each sees the installation's
                  files and may delete the ones they uploaded themselves. */}
              {(currentUser.userType == UserType.admin ||
                currentUser.userType == UserType.partner ||
                currentUser.userType == UserType.client) && (
                <Route
                  path={routes.documents}
                  element={
                    <DocumentsTab
                      installationId={props.current_installation.id}
                      product={props.current_installation.product}
                    />
                  }
                />
              )}

              {currentUser.userType == UserType.admin &&
                CHECKLIST_ENABLED_PRODUCTS.has(props.current_installation.product) && (
                  <Route
                    path={routes.checklist}
                    element={
                      <InstallationChecklistTab
                        installationId={props.current_installation.id}
                      />
                    }
                  />
                )}

              {(currentUser.userType == UserType.admin || currentUser.userType == UserType.partner) &&
                props.current_installation.product === 2 && (
                  <Route
                    path={routes.onSiteChecklist}
                    element={
                      <OnSiteChecklistTab
                        installationId={props.current_installation.id}
                        installation={props.current_installation}
                      />
                    }
                  />
                )}

              <Route
                path={'*'}
                element={<Navigate to={dataCollectionDisabled ? routes.information : routes.live}></Navigate>}
              />
            </Routes>
          </Grid>
        </Card>
      </Grid>
    </>
  );
}

export default SodioHomeInstallation;
